import React, { useEffect, useRef, type JSX, useState } from 'react';
import clsx from 'clsx';
import { PhoneCall, Play, Wifi } from 'lucide-react';
import { Button } from '@core-ui/Components/ui/button';
import { Label } from '@core-ui/Components/ui/label.tsx';
import { useTranslation } from 'react-i18next';
import TestSoundInternetDialog from '@ui/shared/TestSoundInternetDialog.tsx';
import { useTestInternetViewModel } from '@utils/testInternetViewModel.ts';

interface Props {
  onStart: () => void | Promise<void>;
  loading?: boolean;
  className?: string;
}

export default function PreSessionScreen ({
  onStart,
  loading = false,
  className,
}: Props): JSX.Element {
  const autoStartedRef = useRef(false);
  const autoTestStartedRef = useRef(false);

  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isInternetTest, setIsInternetTest] = useState(false);
  const { t } = useTranslation();
  const { startAutoTest } = useTestInternetViewModel();

  useEffect(() => {
    if (loading && !autoStartedRef.current) {
      autoStartedRef.current = true;
      void onStart();
    }
  }, [loading, onStart]);

  useEffect(() => {
    if (autoTestStartedRef.current) {
      return;
    }
    autoTestStartedRef.current = true;

    const runTest = async (): Promise<void> => {
      await startAutoTest();
    };

    void runTest();
  }, [startAutoTest]);

  return (
    <div className={clsx('flex min-h-0', className)}>
      <div className="flex-1 flex flex-col items-center justify-center">
        <Button
          size="lg"
          onClick={() => {
            void onStart();
          }}
          aria-busy={loading}
          className={clsx(
            'relative h-36 w-36 md:h-44 md:w-44 rounded-full text-base md:text-lg font-semibold',
            'bg-card hover:bg-primary/10 border-2 border-primary/30 shadow-xl hover:shadow-primary/20',
            'transition-all duration-300',
          )}
        >
          <div className="absolute inset-0 rounded-full ring-[12px] md:ring-[20px] ring-primary/20" />
          <div className={clsx('flex flex-col items-center justify-center', loading ? 'animate-spin' : '', 'mb-2')}>
            <PhoneCall
              className="mb-2 !h-7 !w-7 md:!h-8 md:!w-8 text-primary"
              strokeWidth={1.8}
            />
            <Label className="font-bold text-lg md:text-xl text-primary cursor-pointer">
              {loading ? t('agentLayout.starting', 'Starting...') : t('agentLayout.start', 'Let\'s start')}
            </Label>
          </div>
        </Button>

        <div className="flex items-center justify-center mt-8 gap-4">
          <Button
            className="bg-muted hover:bg-primary/20 dark:hover:bg-muted/70 border border-border"
            onClick={() => {
              setIsDialogOpen(true);
              setIsInternetTest(false);
            }}
            disabled={false}
          >
            <Play className="text-primary hover:"/>
            <Label className="text-md md:text-md text-foreground cursor-pointer">
              {t('agentLayout.testSound')}
            </Label>
          </Button>
          <Button
            className="bg-muted hover:bg-primary/20 dark:hover:bg-muted/70 border border-border"
            onClick={() => {
              setIsDialogOpen(true);
              setIsInternetTest(true);
            }}
          >
            <Wifi className="text-primary" />
            <Label className="text-md text-foreground cursor-pointer">
              {t('agentLayout.testConnection')}
            </Label>
          </Button>
        </div>
      </div>
      <TestSoundInternetDialog
        open={isDialogOpen}
        setOpen={setIsDialogOpen}
        isInternetTest={isInternetTest}
      />
    </div>
  );
}